import React, { type JSX } from "react"
import { z } from "zod"
import { IDynamoQueryResult, TContact } from "@/schemas/contacts"

// Template Base Stats
export const TemplateStatsSchema = z.object({
  delivered: z.coerce.number().int(),
  replied: z.coerce.number().int(),
  repliedPercentage: z.coerce.string(),
})
export type ITemplateStats = z.infer<typeof TemplateStatsSchema>

// Template Email Stats
export const EmailTemplateStatsSchema = TemplateStatsSchema.extend({
  opened: z.coerce.number().int(),
  clicked: z.coerce.number().int(),
  openedPercentage: z.coerce.string(),
  clickedPercentage: z.coerce.string(),
})
export type IEmailTemplateStats = z.infer<typeof EmailTemplateStatsSchema>

// Template SMS Stats
export const SMSTemplateStatsSchema = TemplateStatsSchema.extend({
  received: z.coerce.number().int(),
  receivedPercentage: z.coerce.string(),
})
export type ISMSTemplateStats = z.infer<typeof SMSTemplateStatsSchema>

// Template Type
export const TemplateTypeEnum = z.enum(["all", "email", "sms", "call"])
export type TemplateType = z.infer<typeof TemplateTypeEnum>

// Template Type without "All"
export const TemplateTypeEnumWithoutAll = z.enum(["email", "sms", "call"])
export type TemplateTypeWithoutAll = z.infer<typeof TemplateTypeEnumWithoutAll>

// Template Share Status
export const TemplateStatusSchema = z.enum(["all", "shared-in", "shared-out", "not-shared"])
export type TemplateStatus = z.infer<typeof TemplateStatusSchema>

// Template Share Status without "All"
export const TemplateStatusWithoutAllSchema = z.enum(["shared-in", "shared-out", "not-shared"])
export type TemplateStatusWithoutAll = z.infer<typeof TemplateStatusWithoutAllSchema>

// Template Chip
export const TemplateChipSchema = z.object({
  id: z.coerce.number().int(),
  name: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  content: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  isShared: z.coerce.boolean(),
  rawContent: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  stats: EmailTemplateStatsSchema.or(SMSTemplateStatsSchema),
  subject: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  rawSubject: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  mediaUrls: z.coerce.string().array().optional(),
  owner: z.object({
    id: z.coerce.number().int(),
    fullName: z.coerce.string(),
    email: z.coerce.string().email(),
  }),
  type: z.coerce
    .string()
    .toLowerCase()
    .trim()
    .transform((t) => {
      switch (t) {
        case "call":
          return TemplateTypeEnumWithoutAll.Enum.call
        case "email":
          return TemplateTypeEnumWithoutAll.Enum.email
        case "sms":
        case "message":
        default:
          return TemplateTypeEnumWithoutAll.Enum.sms
      }
    }),
})
export type ITemplateChip = z.infer<typeof TemplateChipSchema>

// Template Chip without Stats
export const TemplateChipWithoutStatsSchema = TemplateChipSchema.omit({
  stats: true,
})
export type ITemplateChipWithoutStats = z.infer<typeof TemplateChipWithoutStatsSchema>

// Template Chip used in API requests
export const TemplateChipForAPISchema = TemplateChipSchema.omit({
  stats: true,
  type: true,
  // isShared: true,
  id: true,
  owner: true,
})
export type ITemplateChipForAPISchema = z.infer<typeof TemplateChipForAPISchema>

// Template Chip used in Send API requests
export const TemplateChipForSendAPISchema = TemplateChipSchema.omit({
  stats: true,
  type: true,
  isShared: true,
  id: true,
  owner: true,
  name: true,
})
export type ITemplateChipForSendAPISchema = z.infer<typeof TemplateChipForSendAPISchema>

// Message Template Chip
export const MessageTemplateChipSchema = TemplateChipSchema.extend({
  isMMS: z.coerce.boolean(),
})
export type IMessageTemplateChip = z.infer<typeof MessageTemplateChipSchema>

/// /////////////////////////////////////////////////////////////////////////////////////////////
// TODO: Review this
/// /////////////////////////////////////////////////////////////////////////////////////////////
export const ContactSchema = z.object({
  personID: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  email: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  phone: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  fullName: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  name: z.string().optional(),
  remoteType: z.string().optional(),
  remoteID: z.string().optional(),
})
export type IContact = z.infer<typeof ContactSchema>

export interface IContactChip extends IContact {
  keyID?: string
  onRemoveChip?: (key: string) => boolean
}

export interface IRoute {
  isIndex?: boolean
  path: string
  element: React.JSX.Element
}

export enum SimulatedMessageType {
  Text = "text",
  Image = "image",
  Typing = "typing",
}

export enum SimulatedMessageDirection {
  Incoming = "incoming",
  Outgoing = "outgoing",
}

export interface ISimulatedMessage {
  type: SimulatedMessageType
  direction: SimulatedMessageDirection
  content?: string | Array<string>
}

export enum SidebarStatus {
  Expanded = "expanded",
  Collapsed = "collapsed",
}

export enum QueryMethods {
  Get = "get",
  Post = "post",
  Put = "put",
  Del = "delete",
}

export enum QueueEvent {
  EditorExtractValues,
  EditorSetValues,
}

export interface QueueItem {
  event: QueueEvent
  args?: unknown
  callback?: (value: unknown) => void
}

export const ServerTemplateSchema = z.object({
  id: z.coerce.number().int(),
  name: z.coerce.string().transform((d) => {
    if (d === "undefined") return ""
    return d
  }),
  type: z.coerce
    .string()
    .toLowerCase()
    .trim()
    .transform((t) => {
      switch (t) {
        case "call":
          return TemplateTypeEnumWithoutAll.Enum.call
        case "email":
          return TemplateTypeEnumWithoutAll.Enum.email
        case "sms":
        case "message":
        default:
          return TemplateTypeEnumWithoutAll.Enum.sms
      }
    }),
  userId: z.coerce.number().int(),
  isShared: z.coerce.boolean(),
  iTemplateChip: TemplateChipSchema.omit({
    stats: true,
    type: true,
    id: true,
    owner: true,
  }),
  stats: EmailTemplateStatsSchema.or(SMSTemplateStatsSchema),
  owner: z
    .object({
      id: z.coerce.number().int(),
      fullName: z.coerce.string(),
      email: z.coerce.string().email(),
    })
    .optional()
    .nullish(),
})
export type IServerTemplate = z.infer<typeof ServerTemplateSchema>
export const ServerTemplatesArraySchema = ServerTemplateSchema.array()
export type IServerTemplatesArray = z.infer<typeof ServerTemplatesArraySchema>

export interface IInjectedDynamo {
  accountId: string
  authorizationToken: string
  tokenTtlInSeconds: number
  url: string
}

export const DynamoRenewalServerResponseDataSchema = z.object({
  authorizationToken: z.string().trim().min(1),
  tokenTtlInSeconds: z.coerce.number().int(),
  accountId: z.coerce.string(),
  url: z.coerce.string().url(),
})
export type IDynamoRenewalServerResponseData = z.infer<typeof DynamoRenewalServerResponseDataSchema>

export interface IDynamoRenewalServerResponse {
  code: number
  dynamoDb?: IDynamoRenewalServerResponseData
  message?: string
}

export interface IDynamoSearchContactsQueryResult extends Omit<IDynamoQueryResult<TContact>, "Iterations"> {}

export interface IInjectedUser {
  accountSignupChannel: string
  accountId: number
  vmdropSetup: boolean
  disableCheckOut: boolean
  checkedIn: boolean
  phoneStatus: string
  remoteHash: string
  id: number
  accountState: string
  dialingSessionsLast30Days: number
  missedNotifications: number
  name: string
  accountDialingSessionsLast30Days: number
  phoneScreenPopLabel: null
  dialingSession: boolean
  lastName: string
  phoneStatusAction: null
  snoozingSince: null
  powerDialer: boolean
  accountCallMinutesLast30Days: number
  accountSignupPlan: string
  companyName: string
  extension: null
  callMinutesLast30Days: number
  accountSeats: number
  instaCall: null
  email: string
  disableRecordingWarning: boolean
  departmentIds: Array<number>
  isOwner: boolean
  dateCreated: number
  isAdmin: boolean
  fullName: string
  canEavesdrop: boolean
  accountSmsEnabled: boolean
}

export interface Vector2 {
  x: number
  y: number
}

export type ComposerModeType = Omit<TemplateType, "all">
export type AppPageType = "composer" | "powerdialer" | "recents" | "library"

export type ImageFormat = "JPG" | "PNG" | "GIF" | "WEBP" | "FLIF" | "CR2" | "TIF" | "BMP" | "JXR" | "PSD" | "ICO" | "BPG" | "JP2" | "JPM" | "JPX" | "HEIC" | "CUR" | "DCM"

export interface IMediaAttachment {
  url: string
  name: string
  format: ImageFormat | "UNKNOWN"
  size: number
  id: number
}

export type FileFormat = ImageFormat | "UNKNOWN"

export interface Recipient {
  name: string
  contactData: string
  personId: string
}

export const TemplateFieldsSchema = z.object({
  id: z.coerce.number(),
  name: z.coerce.string(),
  stats: EmailTemplateStatsSchema.or(SMSTemplateStatsSchema),
  status: TemplateStatusWithoutAllSchema,
  isOwner: z.coerce.boolean(),
  type: TemplateTypeEnumWithoutAll,
  original: TemplateChipSchema,
})

export type TemplateFields = z.infer<typeof TemplateFieldsSchema>

export const ExtendedServerTemplateDataSchema = TemplateChipSchema.extend({
  sharedBadge: z.enum(["No", "With me", "By me"]),
  statsText: z.string(),
})
export type TExtendedServerTemplateData = z.infer<typeof ExtendedServerTemplateDataSchema>
